import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AIConvoThinkingLoaderComponent } from '@modules/ai-convo/components/ai-convo-thinking-loader/ai-convo-thinking-loader.component';
import { AIConvoThinkingDisplayController } from '@modules/ai-convo/helpers/ai-convo-thinking-display';
import {
    AI_CONVO_THINKING_GENERIC_KEYS,
    getThinkingLabel,
} from '@modules/ai-convo/helpers/ai-convo-thinking.utils';
import {
    AIConvoThinkingDisplay,
    AIConvoThinkingStep,
} from '@modules/ai-convo/models/ai-convo-thinking.model';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'interacta-ai-convo-thinking-line',
    templateUrl: './ai-convo-thinking-line.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AIConvoThinkingLoaderComponent, TranslatePipe],
})
export class AIConvoThinkingLineComponent {
    /** Tool steps received so far for the question being answered, in arrival order. */
    readonly steps = input.required<AIConvoThinkingStep[]>();
    readonly isDark = input<boolean>(false);

    private readonly display = signal<AIConvoThinkingDisplay>({
        kind: 'generic',
        index: 0,
    });
    private readonly sequence = signal(0);
    /** Thoughts that reached the screen: the loader turns once for each of those, not for each one received. */
    protected readonly shownThoughts = signal(0);

    /** One entry at a time: a new id makes the text render afresh and fade in. */
    readonly current = computed(() => {
        const display = this.display();
        const label =
            display.kind === 'generic'
                ? {
                      key: AI_CONVO_THINKING_GENERIC_KEYS[display.index],
                      params: {},
                  }
                : getThinkingLabel(display.step, 'present');

        return label
            ? [
                  {
                      id: this.sequence(),
                      ...label,
                      announce:
                          display.kind === 'thought' || display.index === 0,
                  },
              ]
            : [];
    });

    constructor() {
        const controller = new AIConvoThinkingDisplayController((display) => {
            this.display.set(display);
            if (display.kind === 'thought') {
                this.shownThoughts.update((value) => value + 1);
            }
            this.sequence.update((value) => value + 1);
        });
        controller.start();

        let received = 0;
        toObservable(this.steps)
            .pipe(takeUntilDestroyed())
            .subscribe((steps) => {
                steps.slice(received).forEach((step) => controller.push(step));
                received = steps.length;
            });

        inject(DestroyRef).onDestroy(() => controller.dispose());
    }
}
